import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Clock, Calendar, BarChart3, Flame, TrendingUp, AlertTriangle, LineChart, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { fmtUSD, safePnl } from '../../utils/formatters';
import { getTradeNetPnL, futuresSpecs } from '../../config';
import { getSpec } from '../../config/futuresSpecs';
import { calcTradeStats, calcDailyStats, calcStreaks, calcDirectionStats, netPnlAccessor } from '../../services/statsCalculator';
import { tradeDateInLastNDays, tradeDateAfter } from '../../services/timeService';

const OverviewTab = ({
  darkMode,
  filteredTrades,
  closedFilteredTrades,
  trades,
  accounts,
  currentTradingDay,
  selectedAccountFilter,
  equityCurveViewMode,
  setEquityCurveViewMode,
  equityCurveAccounts,
  setEquityCurveAccounts,
  activeAccountsOnly,
  includePassedInStats,
  timePeriodFilter,
  getAccountById,
  setActiveTab,
}) => {
  return (
          <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
            <h2 className={`text-xl font-bold mb-4 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard</h2>
            {filteredTrades.length === 0 ? (
              <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Welcome to your trading journal! Start by adding some trades to see your performance analytics.
              </p>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(() => {
                    const getPnl = netPnlAccessor(accounts);
                    const { bestDay, worstDay } = calcDailyStats(closedFilteredTrades, getPnl);
                    const { totalTrades, avgPnL } = calcTradeStats(closedFilteredTrades, getPnl);

                    return (
                      <>
                        <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'}`}>
                          <h3 className={`font-semibold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Best Day</h3>
                          <div className={`text-2xl font-bold ${bestDay >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {bestDay >= 0 ? '' : '-'}${fmtUSD(bestDay)}
                          </div>
                        </div>
                        <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'}`}>
                          <h3 className={`font-semibold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Worst Day</h3>
                          <div className={`text-2xl font-bold ${worstDay >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {worstDay >= 0 ? '' : '-'}${fmtUSD(worstDay)}
                          </div>
                        </div>
                        <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'}`}>
                          <h3 className={`font-semibold mb-2 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Avg Trade</h3>
                          <div className={`text-2xl font-bold ${totalTrades > 0 && avgPnL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {totalTrades > 0 ? `${avgPnL >= 0 ? '' : '-'}$${fmtUSD(avgPnL)}` : '$0.00'}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Today's Performance - Only shows when there are trades today (futures: 6pm boundary) */}
                {(() => {
                  const todaysTrades = closedFilteredTrades.filter(t => t.date === currentTradingDay);
                  if (todaysTrades.length === 0) return null;

                  // Calculate comprehensive stats
                  let todayPnL = 0;
                  let totalR = 0;
                  let rCount = 0;
                  let todayWins = 0;
                  let todayLosses = 0;
                  
                  todaysTrades.forEach(trade => {
                    const tradeAccount = accounts.find(acc => acc.id === trade.accountId);
                    const { netPnl } = getTradeNetPnL(trade, tradeAccount);
                    const pnl = isNaN(netPnl) ? 0 : netPnl;
                    todayPnL += pnl;
                    
                    if (pnl > 0) todayWins++;
                    else if (pnl < 0) todayLosses++;
                    
                    // Calculate R-multiple if stop loss is available
                    if (trade.stopLoss && trade.entry) {
                      const risk = Math.abs(parseFloat(trade.entry) - parseFloat(trade.stopLoss));
                      if (risk > 0) {
                        const spec = getSpec(trade.symbol);
                        if (spec) {
                          const riskDollars = (risk / spec.tickSize) * spec.tickValue * (parseInt(trade.quantity) || 1);
                          if (riskDollars > 0) {
                            totalR += pnl / riskDollars;
                            rCount++;
                          }
                        }
                      }
                    }
                  });
                  
                  const todayWinRate = todaysTrades.length > 0 ? (todayWins / todaysTrades.length * 100) : 0;
                  const avgR = rCount > 0 ? totalR / rCount : 0;
                  
                  // Calculate streak (consecutive winning/losing days)
                  const allDates = [...new Set(closedFilteredTrades.map(t => t.date))].sort().reverse();
                  let streak = 0;
                  let streakType = null; // 'win' or 'loss'
                  
                  for (const date of allDates) {
                    const dayTrades = closedFilteredTrades.filter(t => t.date === date);
                    let dayPnL = 0;
                    dayTrades.forEach(trade => {
                      const tradeAccount = accounts.find(acc => acc.id === trade.accountId);
                      const { netPnl } = getTradeNetPnL(trade, tradeAccount);
                      dayPnL += isNaN(netPnl) ? 0 : netPnl;
                    });
                    
                    const isWinDay = dayPnL >= 0;
                    
                    if (streakType === null) {
                      streakType = isWinDay ? 'win' : 'loss';
                      streak = 1;
                    } else if ((streakType === 'win' && isWinDay) || (streakType === 'loss' && !isWinDay)) {
                      streak++;
                    } else {
                      break;
                    }
                  }
                  
                  // Format today's date
                  const todayFormatted = new Date(currentTradingDay + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                  
                  return (
                    <div>
                      <h3 className={`text-lg font-semibold mb-3 text-center transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Today’s Performance
                      </h3>
                      <div className={`p-4 rounded-xl border-2 transition-colors duration-300 ${
                        todayPnL >= 0 
                          ? darkMode ? 'bg-green-900/20 border-green-500/50' : 'bg-green-50 border-green-300'
                          : darkMode ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-300'
                      }`}>
                        {/* Header with date */}
                        <div className={`text-center text-lg font-semibold mb-4 pb-3 border-b ${
                          darkMode ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                        }`}>
                          {todayFormatted}
                        </div>
                        
                        {/* Stats Grid */}
                        <div className="grid grid-cols-6 gap-4 text-center">
                          {/* P&L */}
                          <div>
                            <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              P&L
                            </div>
                            <div className={`text-2xl font-bold ${todayPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {todayPnL >= 0 ? '+$' : '-$'}{fmtUSD(todayPnL)}
                            </div>
                          </div>
                          
                          {/* Trades */}
                          <div>
                            <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              Trades
                            </div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {todaysTrades.length}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span className="text-green-500">{todayWins}W</span>
                              {' / '}
                              <span className="text-red-500">{todayLosses}L</span>
                            </div>
                          </div>
                          
                          {/* Win Rate */}
                          <div>
                            <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              Win Rate
                            </div>
                            <div className={`text-2xl font-bold ${todayWinRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                              {todayWinRate.toFixed(0)}%
                            </div>
                          </div>
                          
                          {/* R-Multiple */}
                          <div>
                            <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              Avg R
                            </div>
                            <div className={`text-2xl font-bold ${avgR >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {avgR >= 0 ? '+' : ''}{avgR.toFixed(2)}R
                            </div>
                          </div>
                          
                          {/* Streak */}
                          <div>
                            <div className={`text-xs uppercase tracking-wide mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              Streak
                            </div>
                            <div className={`text-2xl font-bold ${streakType === 'win' ? 'text-green-500' : 'text-red-500'}`}>
                              {streak} {streakType === 'win' ? '🔥' : '❄️'}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {streakType === 'win' ? 'green' : 'red'} day{streak !== 1 ? 's' : ''}
                            </div>
                          </div>
                          
                          {/* Journal Link */}
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => {
                                setActiveTab('journal');
                              }}
                              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                                darkMode
                                  ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_14px_rgba(59,130,246,0.25)]'
                                  : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                              }`}
                            >
                              View Journal →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Performance by Session */}
                <div>
                  <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Clock className="inline mr-2" size={20} />
                    Performance by Session
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {(() => {
                      // Get user's timezone offset from ET (Eastern Time)
                      // ET is UTC-5 (EST) or UTC-4 (EDT)
                      const now = new Date();
                      const etOffset = -5; // Base EST offset (we'll use standard time for simplicity)
                      const localOffset = -now.getTimezoneOffset() / 60; // Local offset in hours from UTC
                      const hoursDiff = localOffset - etOffset; // Difference from ET
                      
                      // Helper to format time for display
                      const formatTime = (hour) => {
                        const adjustedHour = hour + hoursDiff;
                        const displayHour = adjustedHour < 0 ? adjustedHour + 24 : adjustedHour >= 24 ? adjustedHour - 24 : adjustedHour;
                        const hourPart = Math.floor(displayHour);
                        const minutes = (displayHour % 1) * 60;
                        const _isPM = hourPart >= 12;
                        const hour12 = hourPart === 0 ? 12 : hourPart > 12 ? hourPart - 12 : hourPart;
                        return minutes > 0 ? `${hour12}:${minutes.toString().padStart(2, '0')}` : `${hour12}`;
                      };
                      
                      const formatTimeRange = (start, end) => {
                        return `${formatTime(start)}-${formatTime(end)}`;
                      };
                      
                      // Define trading sessions (ET times - these are when markets operate)
                      const sessions = [
                        { id: 'premarket', name: 'Pre-Market', start: 4, end: 9.5 },
                        { id: 'open', name: 'Open', start: 9.5, end: 10 },
                        { id: 'morning', name: 'Mid-Morning', start: 10, end: 12 },
                        { id: 'lunch', name: 'Lunch', start: 12, end: 13 },
                        { id: 'afternoon', name: 'Afternoon', start: 13, end: 15 },
                        { id: 'close', name: 'Power Hour', start: 15, end: 16 }
                      ];
                      
                      // Helper to get session from entry time (entry times are stored in local time)
                      const getSession = (entryTime) => {
                        if (!entryTime) return null;
                        const [hours, minutes] = entryTime.split(':').map(Number);
                        // Convert local time to ET for session matching
                        const localDecimal = hours + minutes / 60;
                        const etDecimal = localDecimal - hoursDiff;
                        return sessions.find(s => etDecimal >= s.start && etDecimal < s.end);
                      };
                      
                      // Calculate stats for each session
                      const sessionStats = sessions.map(session => {
                        const sessionTrades = closedFilteredTrades.filter(t => {
                          const tradeSession = getSession(t.entryTime);
                          return tradeSession?.id === session.id;
                        });
                        
                        const stats = calcTradeStats(sessionTrades);

                        return {
                          ...session,
                          localTimeRange: formatTimeRange(session.start, session.end),
                          trades: stats.totalTrades,
                          pnl: stats.totalPnL,
                          winRate: stats.totalTrades > 0 ? stats.winRate.toFixed(0) : 0
                        };
                      });
                      
                      return sessionStats.map(session => (
                        <div 
                          key={session.id}
                          className={`p-3 rounded-lg border transition-colors duration-300 ${
                            session.trades === 0
                              ? darkMode ? 'bg-gray-800/30 border-gray-700/50' : 'bg-gray-100/50 border-gray-200'
                              : session.pnl >= 0
                                ? darkMode ? 'bg-green-900/20 border-green-700/50' : 'bg-green-50 border-green-200'
                                : darkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {session.name} <span className="opacity-60">({session.localTimeRange})</span>
                          </div>
                          <div className={`text-lg font-bold ${
                            session.trades === 0
                              ? darkMode ? 'text-gray-500' : 'text-gray-400'
                              : session.pnl >= 0 ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {session.trades === 0 ? '-' : `${session.pnl >= 0 ? '+' : '-'}$${Math.abs(session.pnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {session.trades > 0 ? (
                              <>{session.trades} trade{session.trades !== 1 ? 's' : ''} • {session.winRate}% WR</>
                            ) : (
                              'No trades'
                            )}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Performance by Day of Week */}
                <div>
                  <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Calendar className="inline mr-2" size={20} />
                    Performance by Day
                  </h3>
                  <div className="grid grid-cols-5 gap-2">
                    {(() => {
                      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                      const dayStats = days.map(day => {
                        const dayTrades = closedFilteredTrades.filter(t => {
                          const date = new Date(t.date + 'T12:00:00');
                          const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                          return dayName === day;
                        });
                        const stats = calcTradeStats(dayTrades);
                        return { day: day.slice(0, 3), trades: stats.totalTrades, pnl: stats.totalPnL, winRate: Math.round(stats.winRate) };
                      });
                      
                      return dayStats.map((stat, i) => (
                        <div key={i} className={`p-3 rounded-lg text-center transition-colors duration-300 ${
                          stat.trades === 0 
                            ? darkMode ? 'bg-gray-800/50' : 'bg-gray-100'
                            : stat.pnl >= 0 
                              ? darkMode ? 'bg-green-900/20' : 'bg-green-50'
                              : darkMode ? 'bg-red-900/20' : 'bg-red-50'
                        }`}>
                          <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {stat.day}
                          </div>
                          <div className={`text-lg font-bold ${
                            stat.trades === 0 
                              ? darkMode ? 'text-gray-500' : 'text-gray-400'
                              : stat.pnl >= 0 ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {stat.trades === 0 ? '-' : `${stat.pnl >= 0 ? '+' : '-'}$${Math.abs(stat.pnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {stat.trades > 0 ? `${stat.trades} • ${stat.winRate}%` : 'No trades'}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Daily Performance & Streaks Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Daily Performance */}
                  <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border-white/10' : 'bg-white/20 backdrop-blur-xs border-white/20'}`}>
                    <h3 className={`font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <BarChart3 className="inline mr-2" size={18} />
                      Daily Performance
                    </h3>
                    {(() => {
                      const { greenDays, redDays, avgDailyPnL, tradesPerDay } = calcDailyStats(closedFilteredTrades);

                      return (
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div>
                            <div className="text-2xl font-bold text-green-500">{greenDays}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Green Days</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-red-500">{redDays}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Red Days</div>
                          </div>
                          <div>
                            <div className={`text-2xl font-bold ${avgDailyPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {avgDailyPnL >= 0 ? '+' : '-'}${Math.abs(avgDailyPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Daily P&L</div>
                          </div>
                          <div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tradesPerDay.toFixed(1)}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Trades/Day</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Streaks */}
                  <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border-white/10' : 'bg-white/20 backdrop-blur-xs border-white/20'}`}>
                    <h3 className={`font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <Flame className="inline mr-2" size={18} />
                      Streaks
                    </h3>
                    {(() => {
                      const { currentStreak, bestWinStreak, worstLossStreak } = calcStreaks(closedFilteredTrades);

                      return (
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div>
                            <div className={`text-2xl font-bold ${currentStreak > 0 ? 'text-green-500' : currentStreak < 0 ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {currentStreak > 0 ? `+${currentStreak}` : currentStreak}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-green-500">{bestWinStreak}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Best Win</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-red-500">{worstLossStreak}</div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Worst Loss</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Direction Analysis */}
                <div>
                  <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <TrendingUp className="inline mr-2" size={20} />
                    Direction Analysis
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {(() => {
                      const { long: longDir, short: shortDir } = calcDirectionStats(closedFilteredTrades);

                      const longPnL = longDir.totalPnL;
                      const shortPnL = shortDir.totalPnL;
                      const longWins = longDir.wins;
                      const longLosses = longDir.losses;
                      const shortWins = shortDir.wins;
                      const shortLosses = shortDir.losses;
                      const longWinRate = longDir.winRate.toFixed(1);
                      const shortWinRate = shortDir.winRate.toFixed(1);
                      const longAvgWinner = longDir.avgWin;
                      const longAvgLoser = longDir.avgLoss;
                      const shortAvgWinner = shortDir.avgWin;
                      const shortAvgLoser = shortDir.avgLoss;
                      const longTrades = closedFilteredTrades.filter(t => t.direction?.toLowerCase() === 'long');
                      const shortTrades = closedFilteredTrades.filter(t => t.direction?.toLowerCase() === 'short');
                      const longBestSetup = longDir.bestSetup;
                      const shortBestSetup = shortDir.bestSetup;

                      return (
                        <>
                          <div className={`p-5 rounded-lg border transition-colors duration-300 ${
                            darkMode ? 'bg-green-900/10 border-green-500/30' : 'bg-green-50 border-green-200'
                          }`}>
                            <div className="flex justify-between items-center mb-2">
                              <span className={`text-lg font-medium flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                                <ArrowUpRight size={22} />
                                Long
                              </span>
                              <span className={`text-2xl font-bold ${parseFloat(longWinRate) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                {longWinRate}%
                              </span>
                            </div>
                            <div className={`text-base mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {longTrades.length} trades • <span className="text-green-500">{longWins}W</span> / <span className="text-red-500">{longLosses}L</span>
                            </div>
                            <div className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span className="text-green-500">Avg Win: +${fmtUSD(longAvgWinner)}</span>
                              <span className="mx-2">|</span>
                              <span className="text-red-500">Avg Loss: -${fmtUSD(longAvgLoser)}</span>
                            </div>
                            <div className={`text-base font-semibold mb-2 ${longPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              Total: {longPnL >= 0 ? '+' : '-'}${fmtUSD(longPnL)}
                            </div>
                            {longBestSetup ? (
                              <div className={`text-sm pt-2 border-t ${darkMode ? 'border-green-500/20 text-gray-300' : 'border-green-200 text-gray-600'}`}>
                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Best Setup:</span>{' '}
                                <span className="font-medium">{longBestSetup[0]}</span>
                                <span className={`ml-2 ${((longBestSetup[1].wins / longBestSetup[1].count) * 100) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                  {((longBestSetup[1].wins / longBestSetup[1].count) * 100).toFixed(0)}% WR
                                </span>
                                <span className={`ml-1 ${longBestSetup[1].pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  • {longBestSetup[1].pnl >= 0 ? '+' : '-'}${fmtUSD(longBestSetup[1].pnl)}
                                </span>
                              </div>
                            ) : (
                              <div className={`text-sm pt-2 border-t ${darkMode ? 'border-green-500/20 text-gray-500' : 'border-green-200 text-gray-400'}`}>
                                Best Setup: <span className="italic">Min 20 trades needed</span>
                              </div>
                            )}
                          </div>
                          <div className={`p-5 rounded-lg border transition-colors duration-300 ${
                            darkMode ? 'bg-red-900/10 border-red-500/30' : 'bg-red-50 border-red-200'
                          }`}>
                            <div className="flex justify-between items-center mb-2">
                              <span className={`text-lg font-medium flex items-center gap-2 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                                <ArrowDownRight size={22} />
                                Short
                              </span>
                              <span className={`text-2xl font-bold ${parseFloat(shortWinRate) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                {shortWinRate}%
                              </span>
                            </div>
                            <div className={`text-base mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {shortTrades.length} trades • <span className="text-green-500">{shortWins}W</span> / <span className="text-red-500">{shortLosses}L</span>
                            </div>
                            <div className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span className="text-green-500">Avg Win: +${fmtUSD(shortAvgWinner)}</span>
                              <span className="mx-2">|</span>
                              <span className="text-red-500">Avg Loss: -${fmtUSD(shortAvgLoser)}</span>
                            </div>
                            <div className={`text-base font-semibold mb-2 ${shortPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              Total: {shortPnL >= 0 ? '+' : '-'}${fmtUSD(shortPnL)}
                            </div>
                            {shortBestSetup ? (
                              <div className={`text-sm pt-2 border-t ${darkMode ? 'border-red-500/20 text-gray-300' : 'border-red-200 text-gray-600'}`}>
                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Best Setup:</span>{' '}
                                <span className="font-medium">{shortBestSetup[0]}</span>
                                <span className={`ml-2 ${((shortBestSetup[1].wins / shortBestSetup[1].count) * 100) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                  {((shortBestSetup[1].wins / shortBestSetup[1].count) * 100).toFixed(0)}% WR
                                </span>
                                <span className={`ml-1 ${shortBestSetup[1].pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  • {shortBestSetup[1].pnl >= 0 ? '+' : '-'}${fmtUSD(shortBestSetup[1].pnl)}
                                </span>
                              </div>
                            ) : (
                              <div className={`text-sm pt-2 border-t ${darkMode ? 'border-red-500/20 text-gray-500' : 'border-red-200 text-gray-400'}`}>
                                Best Setup: <span className="italic">Min 20 trades needed</span>
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Recent Trades</h3>
                  <div className="space-y-2">
                    {[...filteredTrades]
                      .sort((a, b) => {
                        // Sort by trading day descending (string compare), then by entry time descending
                        const dateCompare = b.date.localeCompare(a.date);
                        if (dateCompare !== 0) return dateCompare;
                        // If same date, sort by entry time
                        if (a.entryTime && b.entryTime) {
                          return b.entryTime.localeCompare(a.entryTime);
                        }
                        return 0;
                      })
                      .slice(0, 5)
                      .map(trade => (
                      <div key={trade.id} className={`flex justify-between items-center p-3 rounded-lg border transition-colors duration-300 ${
                        darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
                      }`}>
                        <div className="flex items-center gap-3">
                          <span className={`font-medium transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {trade.symbol}
                            {trade.dayBoundaryClosed && (
                              <AlertTriangle className="w-3 h-3 text-orange-500 inline ml-1" title="Position held past 5pm ET" />
                            )}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            trade.direction?.toLowerCase() === 'long' 
                              ? 'bg-green-500/20 text-green-500'
                              : trade.direction?.toLowerCase() === 'short'
                                ? 'bg-red-500/20 text-red-500'
                                : darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {trade.direction || '-'}
                          </span>
                          <span className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{trade.setup}</span>
                          <span className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{trade.date}</span>
                        </div>
                        <span className={`font-bold ${safePnl(trade) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {safePnl(trade) >= 0 ? '' : '-'}${fmtUSD(safePnl(trade))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Equity Curve Chart */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className={`text-lg font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <LineChart className="inline mr-2" size={20} />
                      Equity Curve
                      {selectedAccountFilter !== 'all' && (
                        <span className={`text-sm font-normal ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ({getAccountById(selectedAccountFilter)?.name || selectedAccountFilter})
                        </span>
                      )}
                    </h3>
                    {/* View Mode Selector */}
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEquityCurveViewMode('current')}
                        className={`px-2 py-1 text-xs rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                          equityCurveViewMode === 'current'
                            ? darkMode
                              ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25'
                              : 'bg-blue-50 border-blue-300 text-blue-700 ring-1 ring-inset ring-blue-200'
                            : darkMode
                              ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20'
                              : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300'
                        }`}
                      >
                        Current Period
                      </button>
                      <button
                        onClick={() => setEquityCurveViewMode('all')}
                        className={`px-2 py-1 text-xs rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                          equityCurveViewMode === 'all'
                            ? darkMode
                              ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25'
                              : 'bg-blue-50 border-blue-300 text-blue-700 ring-1 ring-inset ring-blue-200'
                            : darkMode
                              ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20'
                              : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300'
                        }`}
                      >
                        All Periods
                      </button>
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'}`}>
                    {(() => {
                      // Filter trades based on view mode and account selection
                      let tradesToShow = [...filteredTrades];
                      
                      // If viewing current period only, filter by period.
                      // resetHistory.date is YYYY-MM-DD; string-compare via timeService
                      // dodges the UTC drift that bit `new Date(t.date) > new Date(resetDate)`.
                      if (equityCurveViewMode === 'current' && selectedAccountFilter !== 'all') {
                        const account = getAccountById(selectedAccountFilter);
                        const lastResetDateStr = account?.resetHistory?.length > 0
                          ? account.resetHistory[account.resetHistory.length - 1].date
                          : null;
                        if (lastResetDateStr) {
                          tradesToShow = tradesToShow.filter(t => tradeDateAfter(t.date, lastResetDateStr));
                        }
                      }
                      
                      // Sort trades chronologically — string compare on trade.date
                      const sortedTrades = tradesToShow.sort(
                        (a, b) => a.date.localeCompare(b.date) || a.id - b.id
                      );
                      
                      // Get starting balance
                      let startingBalance = 50000; // Default
                      if (selectedAccountFilter !== 'all') {
                        const account = getAccountById(selectedAccountFilter);
                        if (account) {
                          startingBalance = account.startingBalance || 50000;
                        }
                      } else if (accounts.length > 0) {
                        // Sum starting balances - respect activeAccountsOnly filter
                        const accountsToSum = activeAccountsOnly
                          ? accounts.filter(acc => acc.status === 'Active' || (includePassedInStats && acc.status === 'Passed'))
                          : accounts;
                        startingBalance = accountsToSum.reduce((sum, acc) => sum + (acc.startingBalance || 50000), 0);
                      }
                      
                      let runningBalance = startingBalance;
                      const equityData = [{
                        date: 'Start',
                        balance: startingBalance,
                        trade: null
                      }];
                      
                      // Group trades by date and calculate daily balance
                      const dailyBalances = {};
                      sortedTrades.forEach(trade => {
                        runningBalance += safePnl(trade);
                        dailyBalances[trade.date] = runningBalance;
                      });
                      
                      // Convert to chart data
                      Object.entries(dailyBalances).forEach(([date, balance]) => {
                        equityData.push({
                          date: date.slice(5), // Show MM-DD format
                          balance: balance,
                          fullDate: date
                        });
                      });
                      
                      if (equityData.length <= 1) {
                        return (
                          <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Add trades to see your equity curve
                          </p>
                        );
                      }
                      
                      const minBalance = Math.min(...equityData.map(d => d.balance));
                      const maxBalance = Math.max(...equityData.map(d => d.balance));
                      const currentBalance = equityData[equityData.length - 1].balance;
                      const totalPnLCalc = currentBalance - startingBalance;
                      const percentChange = ((currentBalance - startingBalance) / startingBalance * 100).toFixed(2);
                      
                      // Get reset history for markers (when viewing all periods)
                      let resetDates = [];
                      if (equityCurveViewMode === 'all' && selectedAccountFilter !== 'all') {
                        const account = getAccountById(selectedAccountFilter);
                        if (account?.resetHistory?.length > 0) {
                          resetDates = account.resetHistory.map(r => r.date.slice(5)); // MM-DD format
                        }
                      }
                      
                      return (
                        <>
                          <div className="flex justify-between items-center mb-4">
                            <div>
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Starting: </span>
                              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>${startingBalance.toLocaleString()}</span>
                            </div>
                            <div>
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current: </span>
                              <span className={`font-bold ${currentBalance >= startingBalance ? 'text-green-600' : 'text-red-600'}`}>
                                ${currentBalance.toLocaleString()} ({percentChange >= 0 ? '+' : ''}{percentChange}%)
                              </span>
                            </div>
                            <div>
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>P&L: </span>
                              <span className={`font-bold ${totalPnLCalc >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {totalPnLCalc >= 0 ? '+' : '-'}${fmtUSD(totalPnLCalc)}
                              </span>
                            </div>
                            {equityCurveViewMode === 'all' && resetDates.length > 0 && (
                              <div className={`text-xs px-2 py-1 rounded-sm ${darkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                                {resetDates.length} reset{resetDates.length > 1 ? 's' : ''} shown
                              </div>
                            )}
                          </div>
                          <ResponsiveContainer width="100%" height={500}>
                            <RechartsLineChart data={equityData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                              <XAxis 
                                dataKey="date" 
                                stroke={darkMode ? '#9ca3af' : '#6b7280'}
                                fontSize={12}
                                tickLine={false}
                              />
                              <YAxis 
                                stroke={darkMode ? '#9ca3af' : '#6b7280'}
                                fontSize={12}
                                tickLine={false}
                                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                                domain={[minBalance * 0.99, maxBalance * 1.01]}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                                  border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                                  borderRadius: '8px',
                                  color: darkMode ? '#ffffff' : '#1f2937'
                                }}
                                formatter={(value) => [`$${value.toLocaleString()}`, 'Balance']}
                                labelFormatter={(label, payload) => {
                                  if (payload && payload[0] && payload[0].payload.fullDate) {
                                    return payload[0].payload.fullDate;
                                  }
                                  return label;
                                }}
                              />
                              <ReferenceLine 
                                y={startingBalance} 
                                stroke={darkMode ? '#6b7280' : '#9ca3af'} 
                                strokeDasharray="5 5"
                                label={{ 
                                  value: 'Start', 
                                  position: 'right',
                                  fill: darkMode ? '#9ca3af' : '#6b7280',
                                  fontSize: 10
                                }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="balance" 
                                stroke={currentBalance >= startingBalance ? '#10b981' : '#ef4444'}
                                strokeWidth={2}
                                dot={{ fill: currentBalance >= startingBalance ? '#10b981' : '#ef4444', strokeWidth: 0, r: 3 }}
                                activeDot={{ r: 5, strokeWidth: 0 }}
                              />
                            </RechartsLineChart>
                          </ResponsiveContainer>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Multi-Account Equity Comparison Chart */}
                {(() => {
                  // Filter accounts based on activeAccountsOnly toggle
                  const comparisonAccounts = activeAccountsOnly
                    ? accounts.filter(acc => acc.status === 'Active' || (includePassedInStats && acc.status === 'Passed'))
                    : accounts;
                  
                  if (comparisonAccounts.length <= 1) return null;
                  
                  return (
                  <div>
                    <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <BarChart3 className="inline mr-2" size={20} />
                      Account Comparison
                    </h3>
                    <div className={`p-4 rounded-lg border transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'}`}>
                      {/* Account Selection */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Show accounts:</span>
                          <button
                            onClick={() => setEquityCurveAccounts(comparisonAccounts.map(a => a.id))}
                            className={`text-xs px-2 py-1 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                              equityCurveAccounts.length === comparisonAccounts.length && comparisonAccounts.every(a => equityCurveAccounts.includes(a.id))
                                ? darkMode
                                  ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25'
                                  : 'bg-blue-50 border-blue-300 text-blue-700 ring-1 ring-inset ring-blue-200'
                                : darkMode
                                  ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20'
                                  : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300'
                            }`}
                          >
                            All
                          </button>
                          <button
                            onClick={() => setEquityCurveAccounts([])}
                            className={`text-xs px-2 py-1 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                              equityCurveAccounts.length === 0
                                ? darkMode
                                  ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25'
                                  : 'bg-blue-50 border-blue-300 text-blue-700 ring-1 ring-inset ring-blue-200'
                                : darkMode
                                  ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20'
                                  : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300'
                            }`}
                          >
                            None
                          </button>
                          <div className="w-px h-4 bg-gray-400 mx-1"></div>
                          {[...comparisonAccounts].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)).map((account, idx) => {
                            const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
                            const color = colors[idx % colors.length];
                            const isSelected = equityCurveAccounts.includes(account.id);
                            return (
                              <button
                                key={account.id}
                                onClick={() => {
                                  if (isSelected) {
                                    setEquityCurveAccounts(equityCurveAccounts.filter(id => id !== account.id));
                                  } else {
                                    setEquityCurveAccounts([...equityCurveAccounts, account.id]);
                                  }
                                }}
                                className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition-all ${
                                  isSelected
                                    ? 'ring-2 ring-offset-1'
                                    : 'opacity-50 hover:opacity-75'
                                } ${darkMode ? 'ring-offset-gray-700' : 'ring-offset-white'}`}
                                style={{ 
                                  backgroundColor: isSelected ? color + '30' : (darkMode ? '#374151' : '#e5e7eb'),
                                  borderColor: color,
                                  color: isSelected ? color : (darkMode ? '#9ca3af' : '#6b7280'),
                                  ringColor: color
                                }}
                              >
                                <span style={{ backgroundColor: color }} className="w-2 h-2 rounded-full"></span>
                                {account.name || account.id}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Chart */}
                      {(() => {
                        const selectedAccounts = comparisonAccounts.filter(a => equityCurveAccounts.includes(a.id)).sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
                        
                        if (selectedAccounts.length === 0) {
                          return (
                            <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Select accounts above to compare their equity curves
                            </p>
                          );
                        }
                        
                        const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
                        
                        // Build equity data for each account
                        const allDates = new Set();
                        const accountEquityData = {};
                        
                        selectedAccounts.forEach((account, _idx) => {
                          // Last reset date for this account (YYYY-MM-DD or null).
                          const lastResetDateStr = account.resetHistory?.length > 0
                            ? account.resetHistory[account.resetHistory.length - 1].date
                            : null;

                          // Filter trades to only include those after the last reset AND within time period.
                          // String compares against trade.date, anchored at the current trading day.
                          const accountTrades = trades.filter(t => {
                            if (t.accountId !== account.id) return false;
                            if (lastResetDateStr && !tradeDateAfter(t.date, lastResetDateStr)) return false;
                            if (timePeriodFilter !== 'all') {
                              const days = parseInt(timePeriodFilter);
                              if (Number.isFinite(days) && days > 0 && !tradeDateInLastNDays(t.date, days)) return false;
                            }
                            return true;
                          });
                          
                          const sortedTrades = [...accountTrades].sort(
                            (a, b) => a.date.localeCompare(b.date) || a.id - b.id
                          );
                          
                          const startingBalance = account.startingBalance || 50000;
                          let runningBalance = startingBalance;
                          const balanceByDate = { 'Start': startingBalance };
                          
                          sortedTrades.forEach(trade => {
                            runningBalance += safePnl(trade);
                            balanceByDate[trade.date] = runningBalance;
                            allDates.add(trade.date);
                          });
                          
                          accountEquityData[account.id] = {
                            account,
                            color: colors[accounts.findIndex(a => a.id === account.id) % colors.length],
                            startingBalance,
                            currentBalance: runningBalance,
                            balanceByDate
                          };
                        });
                        
                        // Create unified chart data
                        const sortedDates = ['Start', ...Array.from(allDates).sort()];
                        const chartData = sortedDates.map(date => {
                          const dataPoint = { date: date === 'Start' ? 'Start' : date.slice(5), fullDate: date };
                          selectedAccounts.forEach(account => {
                            const equityInfo = accountEquityData[account.id];
                            // Find the most recent balance up to this date
                            let balance = equityInfo.startingBalance;
                            const dates = Object.keys(equityInfo.balanceByDate).filter(d => d <= date || d === 'Start');
                            if (dates.length > 0) {
                              const latestDate = dates[dates.length - 1];
                              balance = equityInfo.balanceByDate[latestDate];
                            }
                            dataPoint[account.id] = balance;
                          });
                          return dataPoint;
                        });
                        
                        // Calculate min/max for Y axis
                        let minVal = Infinity, maxVal = -Infinity;
                        chartData.forEach(d => {
                          selectedAccounts.forEach(a => {
                            if (d[a.id] !== undefined) {
                              minVal = Math.min(minVal, d[a.id]);
                              maxVal = Math.max(maxVal, d[a.id]);
                            }
                          });
                        });
                        
                        return (
                          <>
                            {/* Account Stats Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                              {selectedAccounts.map(account => {
                                const data = accountEquityData[account.id];
                                const pnl = data.currentBalance - data.startingBalance;
                                const pctChange = ((pnl / data.startingBalance) * 100).toFixed(1);
                                return (
                                  <div 
                                    key={account.id}
                                    className={`p-2 rounded-sm text-center ${darkMode ? 'bg-gray-600' : 'bg-white'}`}
                                    style={{ borderLeft: `3px solid ${data.color}` }}
                                  >
                                    <div className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {account.name || account.id}
                                    </div>
                                    <div className={`font-bold ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                      {pnl >= 0 ? '+' : '-'}${Math.abs(pnl).toLocaleString()}
                                    </div>
                                    <div className={`text-xs ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {pctChange >= 0 ? '+' : ''}{pctChange}%
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <ResponsiveContainer width="100%" height={600}>
                              <RechartsLineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                                <XAxis 
                                  dataKey="date" 
                                  stroke={darkMode ? '#9ca3af' : '#6b7280'}
                                  fontSize={12}
                                  tickLine={false}
                                />
                                <YAxis 
                                  stroke={darkMode ? '#9ca3af' : '#6b7280'}
                                  fontSize={12}
                                  tickLine={false}
                                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                                  domain={[minVal * 0.98, maxVal * 1.02]}
                                />
                                <Tooltip 
                                  contentStyle={{
                                    backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                                    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                                    borderRadius: '8px',
                                    color: darkMode ? '#ffffff' : '#1f2937'
                                  }}
                                  formatter={(value, name) => {
                                    const account = accounts.find(a => a.id === name);
                                    return [`$${value.toLocaleString()}`, account?.name || name];
                                  }}
                                  labelFormatter={(label, payload) => {
                                    if (payload && payload[0] && payload[0].payload.fullDate) {
                                      return payload[0].payload.fullDate === 'Start' ? 'Starting Balance' : payload[0].payload.fullDate;
                                    }
                                    return label;
                                  }}
                                />
                                {selectedAccounts.map(account => (
                                  <Line 
                                    key={account.id}
                                    type="monotone" 
                                    dataKey={account.id}
                                    name={account.id}
                                    stroke={accountEquityData[account.id].color}
                                    strokeWidth={2}
                                    dot={{ fill: accountEquityData[account.id].color, strokeWidth: 0, r: 2 }}
                                    activeDot={{ r: 5, strokeWidth: 0 }}
                                    connectNulls
                                  />
                                ))}
                              </RechartsLineChart>
                            </ResponsiveContainer>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  );
                })()}
              </div>
            )}
          </div>
  );
};

export default OverviewTab;
